import type { MeshData, RvtElement, RvtInfo, RvtLevel, Skipped } from '@manudev/rvt';
import { create } from 'zustand';
import { MODELS_BASE } from '@/hero/preload';
import { saveBytes } from '@/store/download';
import type { RvtEvent, RvtRequest } from './rvt.worker';

export const SAMPLE = { url: MODELS_BASE + 'rvt/sample.rvt', name: 'rac_basic_sample_project.rvt' };

export type RvtStatus = 'idle' | 'reading' | 'meshing' | 'ready' | 'error';

interface RvtState {
  status: RvtStatus;
  file: { name: string; size: number } | null;
  info: RvtInfo | null;
  thumbnail: string | null;
  elements: RvtElement[];
  levels: RvtLevel[];
  records: number;
  meshes: MeshData[];
  skipped: Skipped[];
  stats: { solids: number; boxes: number; ms: number } | null;
  startedAt: number;
  // open to last mesh, what the visitor waited
  loadMs: number | null;
  error: string | null;
  exporting: boolean;
  // terrain, planting and rooms in the 3D view
  context: boolean;
  setContext: (context: boolean) => void;
  open: (file: File) => void;
  loadSample: () => void;
  downloadGlb: () => void;
}

let worker: Worker | null = null;
let token = 0;
// the worker is busy for seconds inside the reconstruction; a new file starts a fresh one
let busy = false;

function ensureWorker(): Worker {
  if (worker) return worker;
  worker = new Worker(new URL('./rvt.worker.ts', import.meta.url), { type: 'module' });
  worker.onmessage = ({ data }: MessageEvent<RvtEvent>) => {
    if (data.token === token) receive(data);
  };
  worker.onerror = (e) => {
    busy = false;
    useRvtStore.setState({ status: 'error', error: e.message || 'the reader stopped' });
  };
  return worker;
}

function start(req: RvtRequest, file: { name: string; size: number }, transfer: Transferable[]) {
  if (busy) {
    worker?.terminate();
    worker = null;
  }
  const w = ensureWorker();
  busy = true;
  const old = useRvtStore.getState().thumbnail;
  if (old) URL.revokeObjectURL(old);
  useRvtStore.setState({
    status: 'reading',
    file,
    info: null,
    thumbnail: null,
    elements: [],
    levels: [],
    records: 0,
    meshes: [],
    skipped: [],
    stats: null,
    startedAt: performance.now(),
    loadMs: null,
    error: null,
    exporting: false,
  });
  w.postMessage(req, transfer);
}

const pending: MeshData[] = [];

function receive(e: RvtEvent): void {
  const set = useRvtStore.setState;
  switch (e.type) {
    case 'info':
      set((s) => ({
        info: e.info,
        file: s.file && { ...s.file, size: e.size },
        thumbnail: e.thumbnail
          ? URL.createObjectURL(new Blob([e.thumbnail], { type: 'image/png' }))
          : null,
        status: 'meshing',
      }));
      break;
    case 'elements':
      set({ elements: e.elements, levels: e.levels, records: e.records });
      pending.length = 0;
      break;
    case 'meshes':
      pending.push(...e.meshes);
      break;
    case 'done':
      busy = false;
      set((s) => ({
        status: 'ready',
        meshes: pending.splice(0),
        skipped: e.skipped,
        stats: e.stats,
        loadMs: performance.now() - s.startedAt,
      }));
      break;
    case 'glb': {
      const name = useRvtStore.getState().file?.name.replace(/\.(rvt|rfa)$/i, '') ?? 'revit';
      saveBytes(e.bytes, `${name}.glb`, 'model/gltf-binary');
      set({ exporting: false });
      break;
    }
    case 'error':
      busy = false;
      set((s) =>
        s.exporting
          ? { exporting: false, error: e.message }
          : { status: 'error', error: e.message },
      );
      break;
  }
}

export const useRvtStore = create<RvtState>((_set, get) => ({
  status: 'idle',
  file: null,
  info: null,
  thumbnail: null,
  elements: [],
  levels: [],
  records: 0,
  meshes: [],
  skipped: [],
  stats: null,
  startedAt: 0,
  loadMs: null,
  error: null,
  exporting: false,
  context: false,
  setContext: (context) => useRvtStore.setState({ context }),
  open: (file) => {
    const t = ++token;
    void file.arrayBuffer().then(
      (bytes) => {
        if (t !== token) return;
        start(
          { type: 'open', token: t, name: file.name, bytes },
          { name: file.name, size: file.size },
          [bytes],
        );
      },
      (err: Error) => useRvtStore.setState({ status: 'error', error: err.message }),
    );
  },
  loadSample: () => {
    const t = ++token;
    start(
      { type: 'openUrl', token: t, name: SAMPLE.name, url: SAMPLE.url },
      { name: SAMPLE.name, size: 0 },
      [],
    );
  },
  downloadGlb: () => {
    if (get().status !== 'ready' || get().exporting || !worker) return;
    useRvtStore.setState({ exporting: true });
    worker.postMessage({ type: 'glb', token } satisfies RvtRequest);
  },
}));
